import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { store } from "utils/store/configureStore";

// Clarity tag => field of the store's saved UTM (the API keeps utm_campaign in campaign_id)
const ATTRIBUTION_TAGS = {
  utm_source: "utm_source",
  utm_medium: "utm_medium",
  utm_campaign: "campaign_id"
};

const accountUtm = () => {
  try {
    return store.getState()?.store?.information?.account?.utm;
  } catch (_) {
    return undefined;
  }
};

// Tags the Clarity session with the store's attribution once the account is
// loaded. Best effort: it never throws.
export const tagClarityAttribution = () => {
  try {
    if (typeof window.clarity !== "function") return;
    const utm = accountUtm();
    if (!utm) return;

    Object.entries(ATTRIBUTION_TAGS).forEach(([tag, field]) => {
      const value = utm[field];
      if (value) window.clarity("set", tag, String(value));
    });
  } catch (_) {
    // attribution tags are optional
  }
};

// Clarity starts a new session after ~30 minutes of inactivity even when the
// tab stays open, and identify/custom tags are per-session — so the login-time
// identify in setupCredentialStorage alone leaves those later sessions
// anonymous. Re-fire it on every route change and whenever the tab becomes
// visible again, so every Clarity session of an authenticated user carries the
// email. The email is read from localStorage, where setupCredentialStorage
// keeps it for the lifetime of the login session.
export const useClarityIdentify = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const email = localStorage.getItem("user_email");
    if (!email || email === "null" || email === "undefined") return;

    const identify = () => {
      if (!window.clarity) return;
      window.clarity("identify", email, null, null, email);
      window.clarity("set", "user_email", email);
      tagClarityAttribution();
    };

    identify();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") identify();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [pathname]);

  // The account (and its UTM) usually loads after the first identify, so tag
  // the session again once it arrives.
  useEffect(() => {
    let lastUtm = accountUtm();
    return store.subscribe(() => {
      const utm = accountUtm();
      if (utm && utm !== lastUtm) tagClarityAttribution();
      lastUtm = utm;
    });
  }, []);
};
